// src/components/admin/mail/MailFolderRail.tsx
import React from 'react';
import styled from 'styled-components';
import { Inbox, Send, Trash2 } from 'lucide-react';
import { MailFolder, MailFolderId } from '../../../types/mail';
import { BORDER, SURFACE } from './mail.styles';

const Rail = styled.nav`
    background: ${SURFACE};
    border-right: 1px solid ${BORDER};
    padding: 0.85rem 0.6rem;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    overflow-y: auto;
    min-width: 0;
    box-sizing: border-box;

    /* Below the three-pane breakpoint the rail folds into a horizontal, scrollable strip
       spanning the full width, rather than eating a third column on a narrow screen. */
    @media (max-width: 1099px) {
        grid-column: 1 / -1;
        flex-direction: row;
        overflow-x: auto;
        overflow-y: hidden;
        border-right: none;
        border-bottom: 1px solid ${BORDER};
        padding: 0.7rem;
    }
`;

const Item = styled.button<{ $active: boolean; $accent: string }>`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    padding: 0.65rem 0.75rem;
    border: none;
    border-radius: 8px;
    background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
    box-shadow: ${(p) => (p.$active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none')};
    color: ${(p) => (p.$active ? p.$accent : '#64748b')};
    font-size: 0.86rem;
    font-weight: ${(p) => (p.$active ? 700 : 500)};
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
    white-space: nowrap;

    &:hover {
        background: #ffffff;
        color: ${(p) => p.$accent};
    }

    &:focus-visible {
        outline: 3px solid ${(p) => p.$accent}55;
        outline-offset: -2px;
    }

    @media (max-width: 1099px) {
        width: auto;
        flex-shrink: 0;
    }
`;

const Label = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Badge = styled.span<{ $accent: string }>`
    background: ${(p) => p.$accent};
    color: #ffffff;
    border-radius: 20px;
    padding: 0.1rem 0.5rem;
    font-size: 0.72rem;
    font-weight: 700;
    min-width: 20px;
    text-align: center;
    flex-shrink: 0;
`;

const ICONS: { [key: string]: React.ComponentType<{ size?: number }> } = {
    inbox: Inbox,
    sent: Send,
    trash: Trash2,
};

interface Props {
    folders: MailFolder[];
    activeFolder: MailFolderId;
    accent: string;
    onSelect: (folder: MailFolderId) => void;
}

const MailFolderRail: React.FC<Props> = ({ folders, activeFolder, accent, onSelect }) => (
    <Rail aria-label="Priečinky">
        {folders.map((f) => {
            const Icon = ICONS[f.id] || Inbox;
            const active = f.id === activeFolder;
            return (
                <Item
                    key={f.id}
                    type="button"
                    $active={active}
                    $accent={accent}
                    onClick={() => onSelect(f.id)}
                    aria-current={active ? 'true' : undefined}
                    aria-label={
                        f.unreadCount > 0
                            ? `${f.displayName}, ${f.unreadCount} neprečítaných`
                            : f.displayName
                    }
                >
                    <Icon size={16} />
                    <Label>{f.displayName}</Label>
                    {f.unreadCount > 0 && <Badge $accent={accent}>{f.unreadCount}</Badge>}
                </Item>
            );
        })}
    </Rail>
);

export default MailFolderRail;
